import axios from "axios"

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true
})

// Prevent concurrent refresh stampede: one inflight refresh, others queue behind it
let refreshPromise: Promise<void> | null = null;
let refreshFailed = false;

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Avoid retrying the refresh request itself
    if (originalRequest.url?.includes("/auth/refresh")) {
      return Promise.reject(error);
    }

    // Only retry once per request, and don't retry if refresh already failed this session
    if (error.response?.status === 401 && !originalRequest._retry && !refreshFailed) {
      originalRequest._retry = true;

      try {
        // If a refresh is already in flight, wait for it instead of firing another
        if (!refreshPromise) {
          refreshPromise = axios.post(
            `${process.env.REACT_APP_SERVER_URL}/auth/refresh`,
            {},
            { withCredentials: true }
          ).then(() => {
            refreshPromise = null;
          }).catch((err) => {
            refreshPromise = null;
            refreshFailed = true;
            throw err;
          });
        }

        await refreshPromise;

        // Retry the original request after refreshing
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error("Error refreshing token:", refreshError);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Reset the failed flag when a fresh login succeeds
export const resetRefreshState = () => {
  refreshFailed = false;
  refreshPromise = null;
};

export default axiosInstance
